import React, { Suspense, useState, useRef, useEffect, useMemo } from 'react';
import { Wishlist } from '../common/Wishlist';
import { AccountIcon } from '../common/AccountIcon';
// CartIcon removed — replaced by inline HeaderBagIcon below.
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { isRopasNavActive } from './topNavActive';
import { useSelector, useDispatch } from 'react-redux';
import { selectCartItems, fetchCart } from '../../store/features/cart';
import { fetchFavorites } from '../../store/features/favorites';
import { openCartSidebar, closeCartSidebar, selectCartSidebarOpen } from '../../store/features/ui';
import { isTokenValid } from '../../utils/jwt-helper';
import { lazyWithRetry } from '../../lazyWithRetry';
import content from '../../data/content.json';
import { getRecentSearches, addRecentSearch, clearRecentSearches } from '../../utils/searchHistory';
import { usePublicStoreSettings } from '../../hooks/usePublicStoreSettings';

// Lazy-load the cart sidebar — only mounted once the customer opens the cart.
// On the homepage the sidebar is never visible, so this strips its code
// (and DeleteIcon, image fallbacks, etc.) from the main bundle.
const CartSidebar = lazyWithRetry(() => import('../CartSidebar/CartSidebar'), 'CartSidebar');

/* ──────────────────────────────────────────────────────────────────
 * Clean header icons — no baked-in white tile rect.
 * The shared <Wishlist>/<AccountIcon>/<CartIcon> embed a 44×44 white
 * rounded rectangle inside the SVG (used by other surfaces like the
 * cart sidebar). The mockup wants them plain on the cream nav, so
 * we redraw them inline here.
 * ──────────────────────────────────────────────────────────────── */
const HeaderSearchIcon = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);
const HeaderAccountIcon = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
  </svg>
);
const HeaderHeartIcon = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const HeaderBagIcon = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 7h12l-1 13a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 7z" />
    <path d="M9 7V5a3 3 0 0 1 6 0v2" />
  </svg>
);

const Navigation = ({ variant = 'default', onMenuOpenChange }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [ropasDropdownOpen, setRopasDropdownOpen] = useState(false);
  // Owner-editable top-nav (Configuración → "Menú N — texto/link", 2026-08).
  // Empty setting = the built-in label/link below, so clearing is always safe.
  const { data: navSettings } = usePublicStoreSettings();
  const navLabel = (i, fallback) => {
    const v = navSettings?.[`nav.item${i}.label`];
    return v && v.trim() ? v.trim() : fallback;
  };
  const navTo = (i, fallback) => {
    const v = navSettings?.[`nav.item${i}.url`];
    return v && v.trim() ? v.trim() : fallback;
  };
  const ropasTimeoutRef = useRef(null);
  const cartSidebarOpen = useSelector(selectCartSidebarOpen);
  const cartItems = useSelector(selectCartItems);
  const cartLength = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );
  const favorites = useSelector((state) => state.favoritesState?.favorites);
  const favoritesCount = favorites?.length || 0;
  // isTokenValid reads localStorage — memoize so it doesn't run on every render
  const isLoggedIn = useMemo(() => isTokenValid(), []);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Top-nav active-state helper — see ./topNavActive.js for the rule.
  const isRopasActive = React.useCallback(
    (target) => isRopasNavActive(location.pathname, location.search, target),
    [location.pathname, location.search]
  );
  const topNavLinkClass = (active) =>
    `transition-colors duration-base hover:text-accent-strong ${active ? 'text-accent-strong font-medium' : ''}`;
  const [searchValue, setSearchValue] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchBoxRef = useRef(null);

  // Load recent searches on mount
  useEffect(() => {
    setRecentSearches(getRecentSearches());
  }, []);

  // Close search dropdown on outside click
  useEffect(() => {
    if (!searchFocused) return;
    const handler = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [searchFocused]);
  const isAuthVariant = variant === 'auth';
  const isDefaultLikeVariant = !isAuthVariant;

  const ropasCategory = content?.categories?.find((cat) => cat.code === 'ROPAS');

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchCart());
      dispatch(fetchFavorites());
    }
  }, [isLoggedIn, dispatch]);

  const handleCartClick = () => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    dispatch(openCartSidebar());
  };

  const handleFavoritesClick = () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    navigate('/favorites');
  };

  const handleRopasMouseEnter = () => {
    if (ropasTimeoutRef.current) {
      clearTimeout(ropasTimeoutRef.current);
      ropasTimeoutRef.current = null;
    }
    setRopasDropdownOpen(true);
  };

  const handleRopasMouseLeave = () => {
    ropasTimeoutRef.current = setTimeout(() => {
      setRopasDropdownOpen(false);
    }, 200);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const term = searchValue.trim();
    if (!term) return;
    setRecentSearches(addRecentSearch(term));
    setSearchFocused(false);
    navigate(`/ropas?search=${encodeURIComponent(term)}`);
  };

  const handleSelectRecent = (term) => {
    setSearchValue(term);
    setRecentSearches(addRecentSearch(term));
    setSearchFocused(false);
    navigate(`/ropas?search=${encodeURIComponent(term)}`);
  };

  const handleClearRecent = () => {
    clearRecentSearches();
    setRecentSearches([]);
  };

  React.useEffect(() => {
    if (onMenuOpenChange) onMenuOpenChange(menuOpen);
  }, [menuOpen, onMenuOpenChange]);

  /* ── Search overlay (icon-driven, mockup-matched) ── */
  const [searchOpen, setSearchOpen] = useState(false);

  // Mockup-matched icon button — plain outline icon, no background, no border.
  // Hit area stays 40-44px for touch comfort even though the visual is just the icon.
  const iconBoxClass =
    'w-10 h-10 flex items-center justify-center text-ink-primary hover:text-accent-strong transition-colors duration-base';
  const iconBoxClassMobile =
    'w-11 h-11 flex items-center justify-center text-ink-primary hover:text-accent-strong transition-colors duration-base';
  useEffect(() => {
    if (!searchOpen) return;
    const onEsc = (e) => {
      if (e.key === 'Escape') setSearchOpen(false);
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [searchOpen]);

  return (
    <nav className="sticky top-0 z-50 bg-bg-base/95 backdrop-blur-sm border-b border-line-soft w-full">
      {/* Desktop layout: nav links LEFT · logo CENTER · icons RIGHT */}
      <div className="relative flex items-center px-6 md:px-10 h-[68px] max-w-container mx-auto md:grid md:grid-cols-[1fr_auto_1fr] md:items-center">
        {/* Hamburger (Mobile/Tablet) */}
        <div className="md:hidden relative z-20">
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className={`${iconBoxClassMobile} focus:outline-none`}
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        {/* Nav Links — desktop, anchored LEFT */}
        {isDefaultLikeVariant && (
          <div className="hidden md:flex items-center min-w-0 md:col-start-1">
            <ul className="flex items-center gap-4 lg:gap-6 xl:gap-7 text-[13px] lg:text-[14px] text-ink-primary flex-nowrap">
              <li>
                <NavLink
                  to={navTo(1, '/ropas?filter=nuevo')}
                  className={() => topNavLinkClass(isRopasActive({ filter: 'nuevo' }))}
                >
                  {navLabel(1, 'Nuevo')}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={navTo(2, '/ropas?orden=destacados')}
                  className={() => topNavLinkClass(isRopasActive({ orden: 'destacados' }))}
                >
                  {navLabel(2, 'Destacados')}
                </NavLink>
              </li>
              <li
                className="relative"
                onMouseEnter={handleRopasMouseEnter}
                onMouseLeave={handleRopasMouseLeave}
              >
                <NavLink
                  to={navTo(3, '/ropas')}
                  className={() => topNavLinkClass(isRopasActive({ exactBase: true }))}
                >
                  {navLabel(3, 'SS2027')}
                </NavLink>
                {ropasDropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-3 w-56 bg-bg-surface border border-line-soft rounded-lg z-50"
                    style={{ boxShadow: '0 4px 24px rgba(42,31,26,0.08)' }}
                    onMouseEnter={handleRopasMouseEnter}
                    onMouseLeave={handleRopasMouseLeave}
                  >
                    <div className="py-2">
                      {ropasCategory?.types?.map((type) => (
                        <Link
                          key={type.id}
                          to={`/ropas?type=${encodeURIComponent(type.name)}`}
                          className="block px-4 py-2 text-[13px] text-ink-secondary hover:text-ink-primary hover:bg-bg-muted transition-colors duration-base"
                          onClick={() => setRopasDropdownOpen(false)}
                        >
                          {type.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </li>
              {/* "Looks" (→ /colecciones) intentionally removed from top nav
                  per 2026-05 brief. /colecciones route + page + backend stay
                  intact for direct-URL access; we just hide the entry point. */}
              <li>
                <NavLink
                  to={navTo(4, '/ropas?categoria=esenciales')}
                  className={() => topNavLinkClass(isRopasActive({ categoria: 'esenciales' }))}
                >
                  {navLabel(4, 'Esenciales')}
                </NavLink>
              </li>
            </ul>
          </div>
        )}

        {/* Logo — CENTER (absolute on desktop & mobile) */}
        <Link
          to="/"
          className="font-display italic text-[26px] lg:text-[28px] text-ink-primary leading-none shrink-0
                     absolute left-1/2 -translate-x-1/2 z-10
                     md:static md:translate-x-0 md:col-start-2 md:justify-self-center md:z-auto
                     hover:text-accent-strong transition-colors duration-base"
        >
          Pinksisly
        </Link>

        {/* Icons — desktop, anchored RIGHT (plain stroke icons per mockup) */}
        {isDefaultLikeVariant && (
          <div className="hidden md:flex items-center gap-4 lg:gap-5 md:col-start-3 md:justify-self-end">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className={iconBoxClass}
              aria-label="Buscar productos"
            >
              <HeaderSearchIcon />
            </button>
            <button
              onClick={() => navigate('/account-details/profile')}
              className={iconBoxClass}
              aria-label="Mi cuenta"
            >
              <HeaderAccountIcon />
            </button>
            <button
              onClick={handleFavoritesClick}
              className={`${iconBoxClass} relative`}
              aria-label="Favoritos"
            >
              <HeaderHeartIcon />
              {isLoggedIn && favoritesCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-accent-strong text-white text-[10px] font-medium rounded-full flex items-center justify-center leading-none">
                  {favoritesCount}
                </span>
              )}
            </button>
            <button
              onClick={handleCartClick}
              className={`${iconBoxClass} relative`}
              aria-label={`Carrito${cartLength > 0 ? ` (${cartLength})` : ''}`}
            >
              <HeaderBagIcon />
              {isLoggedIn && cartLength > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-ink-primary text-white text-[10px] font-medium rounded-full flex items-center justify-center leading-none">
                  {cartLength}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Auth Buttons (desktop) */}
        {isAuthVariant && (
          <ul className="hidden md:flex gap-3 ml-auto md:order-3">
            <li>
              <NavLink
                to="/login"
                className={({ isActive }) =>
                  `text-[13px] px-5 py-2 border rounded-md transition-colors duration-base ${isActive ? 'border-accent-strong text-accent-strong' : 'border-line text-ink-primary hover:border-ink-primary'}`
                }
              >
                Iniciar sesión
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/register"
                className={({ isActive }) =>
                  `text-[13px] px-5 py-2 border rounded-md transition-colors duration-base ${isActive ? 'bg-accent-strong border-accent-strong text-white' : 'bg-ink-primary border-ink-primary text-white hover:bg-ink-secondary'}`
                }
              >
                Registrarse
              </NavLink>
            </li>
          </ul>
        )}

        {/* Mobile icons — mockup-matched: search + cart only.
            Favorites / account moved into the hamburger menu below to keep
            the header light on small screens. */}
        {isDefaultLikeVariant && (
          <div className="flex md:hidden items-center gap-3 ml-auto">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className={iconBoxClassMobile}
              aria-label="Buscar productos"
            >
              <HeaderSearchIcon size={22} />
            </button>
            <button
              onClick={handleCartClick}
              className={`${iconBoxClassMobile} relative`}
              aria-label={`Carrito${cartLength > 0 ? ` (${cartLength})` : ''}`}
            >
              <HeaderBagIcon size={22} />
              {isLoggedIn && cartLength > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-ink-primary text-white text-[10px] font-medium rounded-full flex items-center justify-center leading-none">
                  {cartLength}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Search overlay — slides down under the nav */}
        {searchOpen && (
          <div
            role="dialog"
            aria-label="Buscar productos"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: '#FAF6F1',
              borderBottom: '1px solid #E8DDD2',
              boxShadow: '0 8px 24px rgba(42,31,26,0.06)',
              padding: '18px 24px',
              zIndex: 60,
            }}
          >
            <div
              ref={searchBoxRef}
              style={{ maxWidth: 720, margin: '0 auto', position: 'relative' }}
            >
              <form
                onSubmit={(e) => {
                  handleSearch(e);
                  setSearchOpen(false);
                }}
              >
                <svg
                  style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 18,
                    height: 18,
                    color: '#9C8B82',
                    pointerEvents: 'none',
                  }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  type="search"
                  autoFocus
                  enterKeyHint="search"
                  autoComplete="off"
                  placeholder="Buscar productos…"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  style={{
                    width: '100%',
                    height: 48,
                    paddingLeft: 42,
                    paddingRight: 42,
                    background: '#fff',
                    border: '1px solid #E8DDD2',
                    borderRadius: 10,
                    fontSize: 15,
                    color: '#2A1F1A',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  aria-label="Cerrar búsqueda"
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 32,
                    height: 32,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6B5950',
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </form>
              {searchFocused && recentSearches.length > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    background: '#fff',
                    border: '1px solid #E8DDD2',
                    borderRadius: 10,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      borderBottom: '1px solid #F1EBE3',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        color: '#9C8B82',
                        fontWeight: 600,
                      }}
                    >
                      Búsquedas recientes
                    </span>
                    <button
                      type="button"
                      onClick={handleClearRecent}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 11,
                        color: '#9C8B82',
                      }}
                    >
                      Borrar
                    </button>
                  </div>
                  {recentSearches.slice(0, 6).map((term, i) => (
                    <button
                      key={`${term}-${i}`}
                      type="button"
                      onClick={() => {
                        handleSelectRecent(term);
                        setSearchOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        padding: '10px 12px',
                        textAlign: 'left',
                        background: 'none',
                        border: 'none',
                        borderBottom:
                          i < Math.min(recentSearches.length, 6) - 1 ? '1px solid #F1EBE3' : 'none',
                        fontSize: 13,
                        color: '#2A1F1A',
                        cursor: 'pointer',
                      }}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#9C8B82"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      {term}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div
          className="md:hidden"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: '#FAF6F1',
            display: 'flex',
            flexDirection: 'column',
            height: '100dvh',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderBottom: '1px solid rgba(42,31,26,0.1)',
              flexShrink: 0,
            }}
          >
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              style={{
                fontFamily: 'var(--font-display, serif)',
                fontStyle: 'italic',
                fontSize: 26,
                color: '#2A1F1A',
                textDecoration: 'none',
                lineHeight: 1,
              }}
            >
              Pinksisly
            </Link>
            <button
              onClick={() => setMenuOpen(false)}
              aria-label="Cerrar menú"
              style={{
                width: 44,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#2A1F1A',
              }}
            >
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Search bar */}
          <div style={{ padding: '16px 24px 0', flexShrink: 0 }}>
            <form
              onSubmit={(e) => {
                handleSearch(e);
                setMenuOpen(false);
              }}
              style={{ position: 'relative' }}
            >
              <svg
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 16,
                  height: 16,
                  color: '#9a8a82',
                  pointerEvents: 'none',
                }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="search"
                enterKeyHint="search"
                autoComplete="off"
                placeholder="Buscar productos…"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{
                  width: '100%',
                  height: 44,
                  paddingLeft: 38,
                  paddingRight: 12,
                  border: '1px solid rgba(42,31,26,0.15)',
                  borderRadius: 8,
                  fontSize: 15,
                  color: '#2A1F1A',
                  background: '#fff',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </form>
          </div>

          {/* Nav links — large, clearly visible */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '8px 0',
              minHeight: 0,
              background: '#FAF6F1',
            }}
          >
            {isDefaultLikeVariant && (
              <>
                {[
                  // Same four owner-editable entries as the desktop bar,
                  // then the fixed help pages.
                  { to: navTo(1, '/ropas?filter=nuevo'), label: navLabel(1, 'Nuevo') },
                  { to: navTo(2, '/ropas?orden=destacados'), label: navLabel(2, 'Destacados') },
                  { to: navTo(3, '/ropas'), label: navLabel(3, 'SS2027') },
                  { to: navTo(4, '/ropas?categoria=esenciales'), label: navLabel(4, 'Esenciales') },
                  { to: '/contacto', label: 'Contacto' },
                  { to: '/preguntas-frecuentes', label: 'Preguntas frecuentes' },
                  { to: '/metodos-de-pago', label: 'Cómo comprar' },
                ].map(({ to, label }) => (
                  <NavLink
                    key={`${to}-${label}`}
                    to={to}
                    onClick={() => setMenuOpen(false)}
                    style={({ isActive }) => ({
                      display: 'block',
                      padding: '18px 24px',
                      fontSize: 20,
                      fontWeight: 400,
                      color: isActive ? '#B8746B' : '#2A1F1A',
                      textDecoration: 'none',
                      borderBottom: '1px solid rgba(42,31,26,0.08)',
                    })}
                  >
                    {label}
                  </NavLink>
                ))}

                {/* Favoritos */}
                <button
                  onClick={() => {
                    handleFavoritesClick();
                    setMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '18px 24px',
                    fontSize: 20,
                    color: '#2A1F1A',
                    background: 'none',
                    border: 'none',
                    borderBottom: '1px solid rgba(42,31,26,0.08)',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <Wishlist />
                  Favoritos
                  {isLoggedIn && favoritesCount > 0 && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        minWidth: 22,
                        height: 22,
                        padding: '0 6px',
                        borderRadius: 11,
                        background: '#B8746B',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {favoritesCount}
                    </span>
                  )}
                </button>

                {/* Mi cuenta */}
                <button
                  onClick={() => {
                    navigate('/account-details/profile');
                    setMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '18px 24px',
                    fontSize: 20,
                    color: '#2A1F1A',
                    background: 'none',
                    border: 'none',
                    borderBottom: '1px solid rgba(42,31,26,0.08)',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <AccountIcon />
                  Mi cuenta
                </button>
              </>
            )}

            {isAuthVariant && (
              <>
                {[
                  { to: '/login', label: 'Iniciar sesión' },
                  { to: '/register', label: 'Registrarse' },
                ].map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setMenuOpen(false)}
                    style={({ isActive }) => ({
                      display: 'block',
                      padding: '18px 24px',
                      fontSize: 20,
                      color: isActive ? '#B8746B' : '#2A1F1A',
                      textDecoration: 'none',
                      borderBottom: '1px solid rgba(42,31,26,0.08)',
                    })}
                  >
                    {label}
                  </NavLink>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Cart Sidebar — lazy-mounted: we only attach the React subtree
          (and pull its chunk) once the customer has opened the cart
          at least once. After that we keep it mounted so re-opens are
          instant (no flicker / re-fetch). */}
      {cartSidebarOpen && (
        <Suspense fallback={null}>
          <CartSidebar isOpen={cartSidebarOpen} onClose={() => dispatch(closeCartSidebar())} />
        </Suspense>
      )}
    </nav>
  );
};

export default Navigation;
